import { Link } from "react-router-dom";
import { Button } from "../components/ui/button"; // Relative path
import { Input } from "../components/ui/input"; // Relative path
import { Label } from "../components/ui/label"; // Relative path
import logoSvg from "../assets/images/logo.svg"; // Relative path
import { useAuth } from "../context/AuthContext"; // Relative path
import { Loader2 } from "lucide-react"; // Import Loader2
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
// toast is handled by AuthContext

const formSchema = z.object({
  email: z.string().email("Invalid email address"),
});

type ForgotPasswordFormValues = z.infer<typeof formSchema>;

const ForgotPasswordPage = () => {
  const { forgotPassword, isLoading } = useAuth();

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    try {
      await forgotPassword(values.email);
      // Success toast is handled by AuthContext
      // Optionally reset form or navigate, but AuthContext handles main feedback
      form.reset();
    } catch (error) {
      // Error toast is handled by AuthContext
      console.error("Forgot password error:", error);
    }
  };

  return (
    <div className="flex-grow bg-gray-50 py-12">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center">
          <img
            src={logoSvg}
            alt="Commercial Property LK Logo"
            className="h-12 w-auto"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Forgot Your Password?
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your email and we'll send a reset link.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <Label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email address
              </Label>
              <div className="mt-1">
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  {...form.register("email")}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-propertylk-blue focus:border-propertylk-blue sm:text-sm"
                  disabled={isLoading}
                />
                {form.formState.errors.email && (
                  <p className="mt-1 text-xs text-red-600">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Button
                type="submit"
                className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-propertylk-blue hover:bg-propertylk-darkblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-propertylk-blue disabled:opacity-75"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Link...
                  </>
                ) : (
                  "Send Password Reset Link"
                )}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-propertylk-blue hover:text-propertylk-darkblue"
            >
              Remember your password? Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
